import React, { Dispatch, SetStateAction } from 'react';
import { Room } from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/RoomConfigurationContent.utils';
import { FormInstance } from 'antd';
import SiteSurveyStore from '@/store/SiteSurveyForm/SiteSurveyStore';
import { PressureTypeFull, TIER_VALUES, STATION_NAME_SUFFIXES } from '@/store/SiteSurveyForm/constants';

export interface SiteConfigurationContentProps {
  siteSurveyStore: SiteSurveyStore;
  isCustomerInformationStepOpen: boolean;
  toggleCustomerInformationStep: () => void;
  renderCustomerInformationStepContent: () => React.ReactNode;
  form: FormInstance;
}

export const handleCompressorRoomsChange = (value: number | 'other', form: FormInstance): void => {
  if (value === 'other') {
    form.setFieldValue('compressorRooms', 'other');
    form.setFieldValue('otherCompressorRooms', 1);
  } else {
    form.setFieldValue('compressorRooms', value);
    form.setFieldValue('otherCompressorRooms', undefined);
  }
};

export const handleOtherCompressorRoomsChange = (value: number | null, form: FormInstance): void => {
  if (value === null) {
    form.setFieldsValue({ compressorRooms: 1, otherCompressorRooms: undefined });
    return;
  }
  if (value < 6) {
    form.setFieldsValue({ compressorRooms: value, otherCompressorRooms: undefined });
    return;
  }

  const validValue = Math.max(6, Math.min(value, 15));
  form.setFieldsValue({ compressorRooms: 'other', otherCompressorRooms: validValue });
};

export const handleEntryPointsChange = (value: number, entryPoints: number, form: FormInstance): void => {
  if (entryPoints === value) {
    return;
  }

  form.setFieldsValue({ entryPoints: value, otherEntryPoints: undefined });
};

export const handleOtherEntryPointsChange = (e: React.ChangeEvent<HTMLInputElement>, form: FormInstance): void => {
  const value = +e.target.value;
  if (value === null) {
    form.setFieldsValue({ entryPoints: 1, otherEntryPoints: undefined });
    return;
  }
  if (value < 6) {
    form.setFieldsValue({ entryPoints: value, otherEntryPoints: undefined });
    return;
  }

  const validValue = Math.max(6, Math.min(value, 15));
  form.setFieldsValue({ entryPoints: 'other', otherEntryPoints: validValue });
};

export const handleTargetPressureChange = (
  value: boolean,
  hasMultipleTargetPressures: boolean,
  form: FormInstance,
  setRooms: Dispatch<SetStateAction<Room[]>>,
): void => {
  if (hasMultipleTargetPressures === value) {
    return;
  }

  form.setFieldValue('hasMultipleTargetPressures', value);

  const defaultType = value ? PressureTypeFull.LOW : PressureTypeFull.MAIN;
  form.setFieldValue('pressureType', defaultType);
  form.setFieldValue(defaultType, form.getFieldValue(defaultType));
  setRooms?.(form.getFieldValue(defaultType)?.rooms || []);

  const newStationNameSuffix = getDefaultPressureName(form);
  form.setFieldValue('stationNameSuffix', newStationNameSuffix);
};

export const handlePressureChanged = (
  value: string,
  form: FormInstance,
  setRooms: Dispatch<SetStateAction<Room[]>>,
): void => {
  form.setFieldValue('pressureType', value);
  form.setFieldValue(value, form.getFieldValue(value));
  setRooms?.(form.getFieldValue(value)?.rooms || []);

  const newStationNameSuffix = getDefaultPressureName(form);
  form.setFieldValue('stationNameSuffix', newStationNameSuffix);
};

const STATION_PRESSURE_MAP: { [key in PressureTypeFull]: string } = {
  [PressureTypeFull.HIGH]: 'HP',
  [PressureTypeFull.LOW]: 'LP',
  [PressureTypeFull.MEDIUM]: 'MP',
  [PressureTypeFull.MAIN]: 'Main',
};

export const getStationPressureName = (pressureType: PressureTypeFull): string =>
  STATION_PRESSURE_MAP[pressureType] ?? 'Main';

export const getSiteNameFromForm = (form: FormInstance): string => {
  const siteAddress = form.getFieldValue('siteAddress') || {};
  const city = siteAddress.city || '';
  const state = siteAddress.state || '';
  const country = siteAddress.country || '';

  let siteName = '';
  if (city) {
    siteName = city;
    if (state) {
      const stateAbbr = state.length >= 2 ? `${state[0]}${state[1]}` : state[0] || '';
      siteName += ` ${stateAbbr}`;
    } else if (country) {
      siteName += ` ${country}`;
    }
  }

  return siteName;
};

export const getDefaultPressureName = (form: FormInstance): string => {
  const tier = form.getFieldValue('tier');

  if (tier === TIER_VALUES.MONITOR_INTERNAL) {
    return STATION_NAME_SUFFIXES.INTERNAL;
  }

  const hasMultipleTargetPressures = form.getFieldValue('hasMultipleTargetPressures');
  const pressureType = form.getFieldValue('pressureType') as PressureTypeFull;

  return hasMultipleTargetPressures && pressureType ? getStationPressureName(pressureType) : 'Main';
};

export const getSiteDisplayNameFromForm = (siteSurveyStore: SiteSurveyStore, form: FormInstance): string => {
  const siteName = getSiteNameFromForm(form);
  const stationNameSuffix = form.getFieldValue('stationNameSuffix') || getDefaultPressureName(form);

  return siteName ? `${siteName} | ${stationNameSuffix}` : stationNameSuffix;
};

export const getStationNameBasePart = (siteSurveyStore: SiteSurveyStore, form: FormInstance): string => {
  let customerName = '';
  const selectedAccountId = form.getFieldValue('account');
  if (selectedAccountId && siteSurveyStore.userData) {
    const selectedCustomer = siteSurveyStore.userData.find(user => user.customerId === selectedAccountId);
    if (selectedCustomer) {
      customerName = selectedCustomer.customerName;
    }
  }

  if (!customerName) {
    customerName =
      siteSurveyStore.customerInformationData.customer?.name ||
      siteSurveyStore.customerInformationData.newCustomerName ||
      '';
  }

  const siteName = getSiteNameFromForm(form);

  if (customerName && siteName) {
    return `${customerName} | ${siteName}`;
  }

  return customerName || siteName || '';
};

export const generateStationNameParts = (siteSurveyStore: SiteSurveyStore, form: FormInstance) => {
  const basePart = getStationNameBasePart(siteSurveyStore, form);
  const stationNameSuffix = form.getFieldValue('stationNameSuffix') || getDefaultPressureName(form);
  const fullName = basePart ? `${basePart} | ${stationNameSuffix}` : stationNameSuffix;

  return {
    basePart,
    fullName,
    suffix: stationNameSuffix,
  };
};
